/**
 * Route guard — only renders the protected tree once the canonical user
 * has loaded from `/me`. States:
 *   1. `loading` — initial restore from storage. Show a loader.
 *   2. `accountDenial` — /me was refused by the identity/access model
 *      (disabled / not-provisioned / org-disabled / email-unverified). Render
 *      the AccountInactive screen, NOT a /login redirect (which the Cognito
 *      cookie would immediately undo) or the verify spinner (user is null).
 *   3. `identityContractError` — /me responded but failed schema
 *      validation (a version skew, not an access decision). Deterministic, so
 *      the verify spinner would hold forever; render the same AccountInactive
 *      shell with sign-out instead.
 *   4. `identityUnhandledError` — any other deterministic /me failure
 *      (e.g. an unexpected 404/422). Same reasoning and treatment as above.
 *   5. `session && !user` — session exists, /me is in flight (or
 *      retrying a genuinely transient failure). Show a loader. This is the
 *      safety-critical state: we MUST NOT render protected UI here because we
 *      don't yet know the canonical role.
 *   6. `!user` after loading — no session (or /me failed with a genuine
 *      auth error and cleared the session). Redirect to /login.
 *
 * Records the requested path in router state so the login screen can
 * bounce the user back after sign-in completes.
 */

import { Center, Loader, Stack, Text } from "@mantine/core";
import { useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { AccountInactive } from "./AccountInactive";
import { OrganizationPicker } from "./OrganizationPicker";
import {
  clearMicrosoftResumeTarget,
  readMicrosoftResumeTarget,
  resumeMicrosoftSession,
} from "./resumeMicrosoftSession";
import { useAuth } from "./useAuth";

/**
 * How many times the restore may re-assert the stashed route.
 *
 * Retaining the target is what makes a second attempt possible when a router index
 * redirect navigates over the first; an unbounded one would fight a route that
 * redirects elsewhere, each redirect looking exactly like that overwrite. Two covers
 * being overwritten once, after which the default landing is the better answer.
 */
const MAX_RESTORE_ATTEMPTS = 2;

/** The boot loader, and the resume's - identical on purpose, since a resume should be
 *  indistinguishable from a cold load. */
function FullScreenLoader() {
  return (
    <Center h="100vh">
      <Loader size="md" />
    </Center>
  );
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const {
    user,
    session,
    loading,
    lastProvider,
    accountDenial,
    organizationSelection,
    identityContractError,
    identityUnhandledError,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const restoreAttempts = useRef(0);

  // Put the user back where the resume took them from. `returnTo` can only name an
  // origin, so the callback lands on `/` and the route has to be restored here.
  //
  // In an effect rather than the render body, because navigating is a side effect and
  // `StrictMode` runs the render body twice.
  //
  // The read peeks rather than consumes - see `readMicrosoftResumeTarget`. Consuming on
  // the first read was the earlier design and was wrong: the restore competes with
  // whatever else navigates on arrival, so a `StrictMode` replay or a router index
  // redirect could overwrite the route with nothing left to retry from. It is cleared
  // below instead, once the router has actually arrived.
  useEffect(() => {
    if (!user) return;
    const target = readMicrosoftResumeTarget();
    if (!target) return;
    if (target === location.pathname + location.search) {
      // Arrived. Nothing else needs it.
      clearMicrosoftResumeTarget();
      return;
    }
    if (restoreAttempts.current >= MAX_RESTORE_ATTEMPTS) {
      clearMicrosoftResumeTarget();
      return;
    }
    restoreAttempts.current += 1;
    navigate(target, { replace: true });
  }, [user, navigate, location.pathname, location.search]);

  // Initial restore from storage — quiet loader, no message.
  if (loading) {
    return <FullScreenLoader />;
  }

  // Account refused by the identity/access model (disabled / not-provisioned /
  // org-disabled / email-unverified). Show the reason and a deliberate sign-out
  // — never the verify spinner (user is null) or a /login redirect (the Cognito
  // cookie would silently re-login into the same failure).
  if (accountDenial) {
    return <AccountInactive code={accountDenial.code} />;
  }

  // Several organizations to act in and none chosen. Answerable, so it gets a
  // picker rather than the terminal screen above — and it is checked before the
  // "no user yet" loader below, because `user` stays null while the refusal
  // stands and the app would otherwise sit on the verify spinner forever.
  if (organizationSelection) {
    return <OrganizationPicker candidates={organizationSelection} />;
  }

  // /me responded but the payload failed schema validation — a
  // frontend/backend version skew, not an identity decision. Retrying can't
  // fix it (same reasoning as account denial), so show the same explanatory
  // screen rather than holding the verify spinner indefinitely.
  if (identityContractError) {
    return <AccountInactive code="CONTRACT_VIOLATION" />;
  }

  // Any other deterministic failure (e.g. an unexpected 404/422) — same
  // reasoning as above: retrying can't fix it, so don't hold the spinner.
  if (identityUnhandledError) {
    return <AccountInactive code="UNKNOWN" />;
  }

  // Have a session but no canonical user yet — /me is loading.
  // The labeled state distinguishes this from initial mount.
  if (session && !user) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="sm">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Verifying your account…
          </Text>
        </Stack>
      </Center>
    );
  }

  if (!user) {
    // A Microsoft session lapsed here, so resume it rather than asking for a
    // password. These sessions seat no refresh cookie and last only as long as the
    // ID token they hold, so this is the ordinary hourly case, not an edge one -
    // and `/session` reports it as `anonymous`, identical to a first-time visitor,
    // which is why the marker exists at all. Entra's own session usually still
    // stands, so the user lands back here one picker click later instead of at a
    // login form. Rendering the form would turn a silent redirect into a visible
    // logout for every Microsoft user, every expiry.
    //
    // Only for `"microsoft"`: a password session with a live refresh cookie is
    // already renewed above, and one without has no business in an OAuth flow it
    // cannot complete.
    if (lastProvider === "microsoft" && resumeMicrosoftSession()) {
      return <FullScreenLoader />;
    }

    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}
